import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';
import { API_BASE_URL } from '../config';
import '../styles/QRCodeDisplay.css';

const QRCodeDisplay = () => {
  const { code } = useParams();
  const [qrData, setQrData] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (code) {
      fetchQRData();
      generateQRImage();
    }
  }, [code]);

  const fetchQRData = async () => {
    try {
      // Use public endpoint (no admin auth required)
      const response = await axios.get(`${API_BASE_URL}/api/qr-codes/${encodeURIComponent(code)}`);
      setQrData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching QR code data:', err);
      // Even if we can't fetch metadata, still show the QR code
      setQrData({ code, type: 'UNKNOWN' });
      setLoading(false);
    }
  };

  const generateQRImage = async () => {
    try {
      const url = await QRCode.toDataURL(code, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        },
        errorCorrectionLevel: 'H'
      });
      setQrImageUrl(url);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code image');
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'REGISTRATION': return '#4CAF50';
      case 'BONUS': return '#2196F3';
      case 'PENALTY': return '#f44336';
      case 'MINIGAME': return '#9C27B0';
      default: return '#666';
    }
  };

  const getTypeLabel = (type) => {
    switch (type?.toUpperCase()) {
      case 'REGISTRATION': return '📝 Registration Code';
      case 'BONUS': return '💰 Bonus Code';
      case 'PENALTY': return '⚠️ Penalty Code';
      case 'MINIGAME': return '🎮 Minigame Code';
      default: return '🔷 QR Code';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="qr-display-page loading">
        <div className="spinner"></div>
        <p>Loading QR Code...</p>
      </div>
    );
  }

  return (
    <div className="qr-display-page">
      <div className="qr-display-container">
        {/* Header */}
        <div className="qr-header" style={{ borderColor: getTypeColor(qrData?.type) }}>
          <h1>{getTypeLabel(qrData?.type)}</h1>
          {qrData?.value && (
            <div className={`qr-value ${qrData.value > 0 ? 'positive' : 'negative'}`}>
              {qrData.value > 0 ? '+' : ''}{formatCurrency(qrData.value)}
            </div>
          )}
        </div>

        {/* QR Code Image */}
        <div className="qr-image-container">
          {qrImageUrl ? (
            <img src={qrImageUrl} alt="QR Code" className="qr-image" />
          ) : (
            <div className="qr-placeholder">
              <p>Generating QR Code...</p>
            </div>
          )}
        </div>

        {/* Code String */}
        <div className="qr-code-string">
          <code>{code}</code>
        </div>

        {/* Status Badge */}
        {qrData && (
          <div className="qr-status">
            {qrData.used ? (
              <span className="status-badge used">
                ❌ USED
                {qrData.used_by && ` by ${qrData.username || 'Unknown'}`}
              </span>
            ) : (
              <span className="status-badge available">
                ✅ AVAILABLE
              </span>
            )}
          </div>
        )}

        {/* Details */}
        {qrData && (
          <div className="qr-details">
            {qrData.description && (
              <div className="detail-row">
                <span className="label">Description:</span>
                <span className="value">{qrData.description}</span>
              </div>
            )}
            {qrData.created_at && (
              <div className="detail-row">
                <span className="label">Created:</span>
                <span className="value">{new Date(qrData.created_at).toLocaleString()}</span>
              </div>
            )}
            {qrData.used_at && (
              <div className="detail-row">
                <span className="label">Used:</span>
                <span className="value">{new Date(qrData.used_at).toLocaleString()}</span>
              </div>
            )}
            {qrData.expires_at && (
              <div className="detail-row">
                <span className="label">Expires:</span>
                <span className="value">{new Date(qrData.expires_at).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="qr-actions no-print">
          <button onClick={handlePrint} className="action-btn print-btn">
            🖨️ Print
          </button>
          <button
            onClick={() => {
              navigator.clipboard.writeText(code);
              alert('Code copied to clipboard!');
            }}
            className="action-btn copy-btn"
          >
            📋 Copy Code
          </button>
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              alert('Link copied to clipboard!');
            }}
            className="action-btn link-btn"
          >
            🔗 Copy Link
          </button>
        </div>

        {/* Instructions */}
        <div className="qr-instructions no-print">
          <p>Scan this QR code with your phone to use it</p>
        </div>
      </div>
    </div>
  );
};

export default QRCodeDisplay;
